import { apiFetch } from '@/shared/api/apiFetch';
import {
  GetPpeSectionsResponseSchema,
  ListPpeCatalogResponseSchema,
  ListPpeNormsResponseSchema,
  GetPpeWidgetSummaryResponseSchema,
  ListPpeEmployeesResponseSchema,
  GetPpeEmployeeProfileResponseSchema,
  GetPpeEmployeeRequiredItemsResponseSchema,
  GetPpeEmployeeIssuancesResponseSchema,
  GetPpeEmployeeHistoryResponseSchema,
  type PpeSectionItem,
  type SizCatalogItem,
  type PpeNormsByPosition,
  type PpeWidgetSummary,
  type PpeEmployeeList,
  type PpeEmployeeProfile,
  type PpeRequiredItem,
  type PpeActiveIssuance,
  type PpeHistoryItem,
} from '../Siz';

// Sections — только то, что вернул GET /v1/ppe/sections; легаси-ключи приводим к §5.1.1
// для совпадения с UI (без подстановки разделов «с клиента»).
const LEGACY_TO_CANONICAL_PPE_SECTION_KEY: Record<string, string> = {
  catalog: 'siz_catalog',
  norms: 'norms_catalog',
  employees: 'employees_ppe',
  issuance: 'employees_ppe',
  returns: 'employees_ppe',
};

export async function fetchSizSections(): Promise<PpeSectionItem[]> {
  const json = await apiFetch<unknown>('/ppe/sections', { method: 'GET' });
  const parsed = GetPpeSectionsResponseSchema.parse(json);
  const seen = new Set<string>();
  const result: PpeSectionItem[] = [];
  for (const item of parsed.sections) {
    const key = LEGACY_TO_CANONICAL_PPE_SECTION_KEY[item.key] ?? item.key;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ key });
  }
  return result;
}

// Catalog
export type CatalogParams = {
  q?: string;
  category?: string;
  page_token?: string;
};

export interface ListPpeCatalogResult {
  items: SizCatalogItem[];
  nextPageToken?: string;
}

export async function fetchSizCatalog(params: CatalogParams = {}): Promise<ListPpeCatalogResult> {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.category) sp.set('category', params.category);
  if (params.page_token) sp.set('page_token', params.page_token);
  const qs = sp.toString();
  const json = await apiFetch<unknown>(`/ppe/catalog${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return ListPpeCatalogResponseSchema.parse(json);
}

// Norms
export type NormsParams = {
  q?: string;
  position_id?: string;
  page_token?: string;
};

export interface ListPpeNormsResult {
  positions: PpeNormsByPosition[];
  nextPageToken?: string;
}

export async function fetchSizNorms(params: NormsParams = {}): Promise<ListPpeNormsResult> {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.position_id) sp.set('position_id', params.position_id);
  if (params.page_token) sp.set('page_token', params.page_token);
  const qs = sp.toString();
  const json = await apiFetch<unknown>(`/ppe/norms${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return ListPpeNormsResponseSchema.parse(json);
}

// Widget
export async function fetchSizWidget(): Promise<PpeWidgetSummary> {
  const json = await apiFetch<unknown>('/ppe/widgets/summary', { method: 'GET' });
  const parsed = GetPpeWidgetSummaryResponseSchema.parse(json);
  return parsed.widget;
}

// Employees
export type SizEmployeesParams = {
  q?: string;
  org_unit_id?: string;
  statuses?: string;
  page_token?: string;
};

export interface ListPpeEmployeesResult {
  employees: PpeEmployeeList[];
  nextPageToken?: string;
}

export async function fetchSizEmployees(params: SizEmployeesParams = {}): Promise<ListPpeEmployeesResult> {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.org_unit_id) sp.set('org_unit_id', params.org_unit_id);
  if (params.statuses) sp.set('statuses', params.statuses);
  if (params.page_token) sp.set('page_token', params.page_token);
  const qs = sp.toString();
  const json = await apiFetch<unknown>(`/ppe/employees${qs ? `?${qs}` : ''}`, { method: 'GET' });
  return ListPpeEmployeesResponseSchema.parse(json);
}

// Employee profile
export async function fetchSizEmployeeProfile(employeeId: string): Promise<PpeEmployeeProfile> {
  const json = await apiFetch<unknown>(`/ppe/employees/${employeeId}`, { method: 'GET' });
  const parsed = GetPpeEmployeeProfileResponseSchema.parse(json);
  return parsed.employee;
}

// Employee required items
export async function fetchSizEmployeeRequiredItems(employeeId: string): Promise<PpeRequiredItem[]> {
  const json = await apiFetch<unknown>(`/ppe/employees/${employeeId}/required-items`, { method: 'GET' });
  const parsed = GetPpeEmployeeRequiredItemsResponseSchema.parse(json);
  return parsed.items;
}

// Employee issuances
export async function fetchSizEmployeeIssuances(employeeId: string): Promise<PpeActiveIssuance[]> {
  const json = await apiFetch<unknown>(`/ppe/employees/${employeeId}/issuances`, { method: 'GET' });
  const parsed = GetPpeEmployeeIssuancesResponseSchema.parse(json);
  return parsed.active;
}

// Employee history
export async function fetchSizEmployeeHistory(employeeId: string): Promise<PpeHistoryItem[]> {
  const json = await apiFetch<unknown>(`/ppe/employees/${employeeId}/history`, { method: 'GET' });
  const parsed = GetPpeEmployeeHistoryResponseSchema.parse(json);
  return parsed.history;
}

// Create issuance (demo restriction - returns 409)
export interface CreateIssuanceRequest {
  employee_id: string;
  siz_item_id: string;
  quantity: string;
  issued_at: string;
  valid_until: string;
  size?: string;
  inventory_number?: string;
  gost_or_tr_ts?: string;
  source?: string;
  out_of_norm_reason?: string;
}

export interface CreateIssuanceResponse {
  issuance_id: string;
  message: string;
}

export async function createSizIssuance(_data: CreateIssuanceRequest): Promise<CreateIssuanceResponse> {
  // Demo mode - will return 409
  const json = await apiFetch<unknown>('/ppe/issuances', { method: 'POST', body: JSON.stringify({}) });
  return json as CreateIssuanceResponse;
}

// Create return (demo restriction - returns 409)
export interface CreateReturnRequest {
  issuance_id: string;
  returned_at: string;
  wear_percent?: number;
  reason: string;
  disposition: string;
}

export interface CreateReturnResponse {
  return_id: string;
  message: string;
}

export async function createSizReturn(_data: CreateReturnRequest): Promise<CreateReturnResponse> {
  // Demo mode - will return 409
  const json = await apiFetch<unknown>('/ppe/returns', { method: 'POST', body: JSON.stringify({}) });
  return json as CreateReturnResponse;
}
